/**
 * @file: ReportViewSummaryStore.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Shared loader for the report read/view status shown on the bottom row of
 * `TaskCardV2`. The backend only exposes a per-order endpoint, so the win is
 * in fetching FEWER of them: a list host reports which order numbers are
 * actually visible and the store fetches only those (once), keeping what it
 * already loaded for cards scrolled out of view.
 */

import { ReportShareViewSummary } from '@euler/model/ReportShareViewSummary';
import { OrderService } from '@euler/services/order.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export type ReportViewSummaryEntry = {
  orderNo: string;
  summary: ReportShareViewSummary | null;
  /** true while the FIRST load for this order is in flight. */
  loading: boolean;
  /** the last attempt failed — retried when the card scrolls back into view. */
  failed?: boolean;
};

export class ReportViewSummaryStore {
  private readonly entries$ = new BehaviorSubject<
    ReadonlyMap<string, ReportViewSummaryEntry>
  >(new Map());
  private readonly inFlight = new Set<string>();
  private visible: ReadonlySet<string> = new Set();
  private disposed = false;

  constructor(private readonly orderService: OrderService) {}

  entry$(orderNo: string): Observable<ReportViewSummaryEntry | undefined> {
    return this.entries$.pipe(
      map(entries => entries.get(orderNo)),
      distinctUntilChanged(),
    );
  }

  peek(orderNo: string): ReportViewSummaryEntry | undefined {
    return this.entries$.getValue().get(orderNo);
  }

  /**
   * Called by the list host whenever the set of on-screen cards changes.
   * Orders never seen before start loading; already-loaded ones are kept.
   */
  setVisible(orderNos: readonly string[]) {
    if (this.disposed) return;
    this.visible = new Set(orderNos);
    for (const orderNo of this.visible) {
      this.load(orderNo, false);
    }
  }

  /** Re-fetch the currently visible orders (screen focus / pull-to-refresh). */
  refreshVisible() {
    if (this.disposed) return;
    for (const orderNo of this.visible) {
      this.load(orderNo, true);
    }
  }

  dispose() {
    this.disposed = true;
    this.inFlight.clear();
    this.entries$.complete();
  }

  private load(orderNo: string, force: boolean) {
    if (this.inFlight.has(orderNo)) return;
    const existing = this.peek(orderNo);
    if (existing && !existing.failed && !force) return;

    this.inFlight.add(orderNo);
    // a reload keeps the previous value on screen — no skeleton flash, and no
    // re-render of the visible cards until the new value actually arrives.
    if (existing == null) {
      this.patch({ orderNo, summary: null, loading: true });
    }

    void (async () => {
      try {
        const summary = await this.orderService.getReportShareViewSummary(
          orderNo,
          { withViewSummary: true, withShareSummary: true },
        );
        if (this.disposed) return;
        this.patch({ orderNo, summary, loading: false });
      } catch (e) {
        console.error('failed to load report share view summary:', e);
        if (this.disposed) return;
        this.patch({
          orderNo,
          summary: existing?.summary ?? null,
          loading: false,
          failed: true,
        });
      } finally {
        this.inFlight.delete(orderNo);
      }
    })();
  }

  private patch(entry: ReportViewSummaryEntry) {
    const next = new Map(this.entries$.getValue());
    next.set(entry.orderNo, entry);
    this.entries$.next(next);
  }
}
